/**
 * Kaartdata van het basisspel.
 *
 * De JSON wordt gegenereerd door `tools/extract-cards.ts` uit het AI-werkblad van het
 * originele spel (zie kennisbank 03). Hij wordt hier bij het laden gevalideerd: liever
 * meteen stuk met een duidelijke melding dan halverwege een potje een ontbrekend veld.
 */
import { z } from 'zod';
import raw from './data/cards-basic.json' with { type: 'json' };
import { RESOURCES } from './domain.js';

export const CardKindSchema = z.enum(['straat', 'dorp', 'stad', 'uitbouw', 'actie', 'gebeurtenis']);
export type CardKind = z.infer<typeof CardKindSchema>;

/** Rode kaarten mogen alleen in een stad, groene ook in een dorp (handboek p. 5). */
export const PlacementSchema = z.enum(['dorp-en-stad', 'alleen-stad', 'geen']);
export type Placement = z.infer<typeof PlacementSchema>;

/** Kosten worden in vijf grondstoffen uitgedrukt; goud komt in het basisspel niet voor. */
const KOSTEN_SOORTEN = ['graan', 'erts', 'hout', 'wol', 'klei'] as const;
export const CardCostSchema = z
  .record(z.enum(KOSTEN_SOORTEN), z.number().int().positive())
  // Waarom een record en geen object: een objectschema herbouwt de kosten in
  // schémavolgorde, en de sleutelvolgorde ís hier betekenis — het is de gedrukte
  // volgorde van de fysieke kaart (Karel de Machtige begint met 3 wol), waar de
  // kostenstrook op tekent. Een record laat de JSON-volgorde ongemoeid; onbekende
  // soorten weigert hij net zo hard als `.strict()` deed. `kostenvolgorde.test.ts`
  // pint de volgorde per kaart vast tegen de kaartexport van Thijs.
  //
  // De transform verandert alleen het type: zod 3 typeert een enum-record als "alle
  // sleutels aanwezig", terwijl een kaart alleen draagt wat hij werkelijk kost.
  .transform((kosten) => kosten as Partial<Record<(typeof KOSTEN_SOORTEN)[number], number>>);
export type CardCost = z.infer<typeof CardCostSchema>;

export const CardSchema = z
  .object({
    id: z.number().int().positive(),
    name: z.string().min(1),
    sourceName: z.string().min(1),
    imageIndex: z.number().int().nonnegative(),
    /** De regeltekst zoals hij op de kaart staat, uit `text/basic.txt`. */
    rules: z.string(),
    kind: CardKindSchema,
    placement: PlacementSchema,
    count: z.number().int().positive(),
    cost: CardCostSchema,
    victoryPoints: z.number().int().nonnegative(),
    commercePoints: z.number().int().nonnegative(),
    strength: z.number().int().nonnegative(),
    tournamentPoints: z.number().int().nonnegative(),
    progress: z.number().int().nonnegative(),
    yieldValue: z.number(),
  })
  .strict();
export type Card = z.infer<typeof CardSchema>;

const CardFileSchema = z.object({
  source: z.string(),
  cards: z.array(CardSchema).min(1),
});

const parsed = CardFileSchema.parse(raw);

/** Alle kaarttypen van het basisspel, op ID gesorteerd. */
export const BASIC_CARDS: readonly Card[] = Object.freeze(parsed.cards);

const byId = new Map(BASIC_CARDS.map((card) => [card.id, card]));
const byName = new Map(BASIC_CARDS.map((card) => [card.name.toLowerCase(), card]));

export function cardById(id: number): Card | undefined {
  return byId.get(id);
}

export function cardByName(name: string): Card | undefined {
  return byName.get(name.toLowerCase());
}

/** De kaarten die samen de vijf uitbouwstapels vullen (dus zonder straat/dorp/stad). */
export function expansionCards(): readonly Card[] {
  return BASIC_CARDS.filter((card) => card.kind === 'uitbouw' || card.kind === 'actie');
}

/** De gebeurteniskaarten (de blauwe stapel). */
export function eventCards(): readonly Card[] {
  return BASIC_CARDS.filter((card) => card.kind === 'gebeurtenis');
}

/** Alle exemplaren van een verzameling kaarten, als losse ID's. */
export function copiesOf(cards: readonly Card[]): number[] {
  return cards.flatMap((card) => Array.from({ length: card.count }, () => card.id));
}

/** Totaal aantal exemplaren, desgewenst gefilterd op soort. */
export function countCopies(kinds?: readonly CardKind[]): number {
  return BASIC_CARDS.filter((card) => kinds === undefined || kinds.includes(card.kind)).reduce(
    (sum, card) => sum + card.count,
    0,
  );
}

/** Kosten optellen tot een volledig grondstoffenoverzicht (ontbrekende soorten = 0). */
export function fullCost(cost: CardCost): Record<(typeof RESOURCES)[number], number> {
  const out = {} as Record<(typeof RESOURCES)[number], number>;
  for (const resource of RESOURCES) {
    out[resource] = resource === 'goud' ? 0 : ((cost as Record<string, number | undefined>)[resource] ?? 0);
  }
  return out;
}
